import posthog from 'posthog-js';
import { ProductType } from '@qred/shared-enums';
import type { ProductTypeLowercase } from '~/schemas/DTO';
import { ClientType, EAnalyticsEvent, FlowTypes, StepStatus } from '~/enums';
import { IAnalyticEventParams, IGtmAnalyticEventParams } from '~/Interfaces';
import { AppDispatch, GetStateFunc } from '..';
import { selectStepConfig as selectCardOfferStepConfig } from '../selectors/cardOffer.selector';
import {
  pushToGtmHelper,
  shouldTrackTimeSpentOnStep,
} from './analyticsActions.utils';

interface IBaseEventProps {
  errorMessage?: string;
  stepActionNumber?: number;
  extraContext?: {};
}

interface IActionEventProps {
  actionName: string;
  stepStatus?: never;
}

interface IStepStatusEventProps {
  actionName?: never;
  stepStatus: StepStatus;
}

type Props = IBaseEventProps & (IActionEventProps | IStepStatusEventProps);

export const trackCardApplicationActions = ({
  actionName,
  stepStatus,
  errorMessage,
  extraContext,
}: Props) => (_dispatch: AppDispatch, getState: GetStateFunc) => {
  const state = getState();

  const {
    cardApplication: {
      clientId,
      form: {
        applicationId,
        applicant: { email, phone },
      },
      currentStepNumber,
      currentStepName,
      stepStartTimestamp,
    },
    intl: { market, language },
  } = state;

  const timeSpentOnStep = shouldTrackTimeSpentOnStep(actionName)
    ? Math.ceil(
        (new Date().getTime() - new Date(stepStartTimestamp).getTime()) / 1000
      )
    : undefined;

  const baseEventProperties: IAnalyticEventParams = {
    eventName: EAnalyticsEvent.OnboardingApplicationFlow,
    product: ProductType.CARD.toLowerCase() as ProductTypeLowercase,
    stepNumber: currentStepNumber,
    ...(clientId && { userId: clientId }),
    orderId: applicationId,
    stepName: currentStepName,
    clientType: ClientType.Unknown,
    market,
    language,
    errorMessage,
    actionName,
    stepStatus,
    timeSpentOnStep,
    timestamp: new Date().toISOString(),
    ...extraContext,
  };

  const gtmEventProperties: IGtmAnalyticEventParams = {
    ...baseEventProperties,
    phoneNumber: phone,
    email,
  };

  if (window.Cypress) {
    window.posthogCapture(
      EAnalyticsEvent.OnboardingApplicationFlow,
      baseEventProperties
    );
  }

  posthog.capture(
    EAnalyticsEvent.OnboardingApplicationFlow,
    baseEventProperties
  );
  pushToGtmHelper(
    EAnalyticsEvent.OnboardingApplicationFlow,
    gtmEventProperties
  );
};

export const trackCardOfferActions = ({
  stepStatus,
  actionName,
  errorMessage,
}: Props) => (_dispatch: AppDispatch, getState: GetStateFunc) => {
  const state = getState();
  const {
    cardOffer: {
      currentStep,
      overview: {
        clientId,
        applicant: { email, phoneNumber },
      },
      stepStartTimestamp,
    },
    intl: { market, language },
  } = state;
  const cardOfferStepConfig = selectCardOfferStepConfig(state);
  const currentStepConfig = cardOfferStepConfig[currentStep - 1];
  const stepName = currentStepConfig?.name;
  const timeSpentOnStep = shouldTrackTimeSpentOnStep(actionName)
    ? Math.ceil(
        (new Date().getTime() - new Date(stepStartTimestamp).getTime()) / 1000
      )
    : undefined;

  const baseEventProperties: IAnalyticEventParams = {
    eventName: EAnalyticsEvent.OnboardingOfferFlow,
    product: ProductType.CARD.toLowerCase() as ProductTypeLowercase,
    stepNumber: currentStep,
    stepName,
    clientType: ClientType.Unknown,
    ...(clientId && { userId: clientId }),
    market,
    language,
    errorMessage,
    actionName,
    stepStatus,
    flowType: FlowTypes.Authenticated,
    timeSpentOnStep,
    timestamp: new Date().toISOString(),
  };

  const gtmEventProperties = {
    ...baseEventProperties,
    phoneNumber,
    email,
  };

  if (window.Cypress) {
    window.posthogCapture(
      EAnalyticsEvent.OnboardingOfferFlow,
      baseEventProperties
    );
  }

  posthog.capture(EAnalyticsEvent.OnboardingOfferFlow, baseEventProperties);
  pushToGtmHelper(EAnalyticsEvent.OnboardingOfferFlow, gtmEventProperties);
};
